import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { VacationApproverReportItem, VacationState, isVacationWaitingApproval } from '../../../../features/vacation/types';
import { SVGS } from '../../../../assets/images';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { approveVacation, cancelVacation, declineVacation, getVacations } from '../../../../features/vacation/slice';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import ActionConfirmDialog from '../action-confirm-dialog';
import { Lx } from '../../../../i18n/consts';
import TSwitch from '../../../../components/shared/form-controls/switch';
import {
    canApproveDeclineAnyVacations,
    canEditVacationWithoutApproval,
    canSeeOtherVacations
} from '../../../../features/profile/entityPermissions';
export { VacationState };

import styles from './vacation-diagram.module.scss';
import popupStyles from '../../../../components/shared/popup-menu/popup-menu.module.scss';
import { useBottomScrollbar } from './useBottomScrollbar';

const isVacationCreationDateAvailable = (date: moment.Moment) => (
    date.isAfter(moment(), 'day')
);

export interface GroupedDiagramItem {
    employeeId: number;
    employeeName: string;
    durations: Duration[];
}
export interface Duration {
    id: number;
    employeeId: number;
    dateFrom: moment.Moment;
    dateTo: moment.Moment;
    state: VacationState;
    approvers: VacationApproverReportItem[];
}
export interface VacationDiagramDay {
    date: moment.Moment;
    numberDayOfMonth: number;
    isDayOff: boolean;
    isShort: boolean;
}

type Props = {
    employees: GroupedDiagramItem[];
    days: VacationDiagramDay[];
    onCreateRange: (employee: GroupedDiagramItem, dateFrom: moment.Moment, dateTo: moment.Moment) => void;
};

type DraftSelection = {
    employeeId: number;
    anchorIndex: number;
    currentIndex: number;
    pendingConfirmation: boolean;
};

type VacationAction = 'cancel' | 'approve' | 'decline';

type VirtualAnchor = {
    getBoundingClientRect: () => DOMRect;
    contextElement?: Element;
};

type OpenMenuState = {
    vacationId: number;
    anchor: VirtualAnchor;
} | null;

function VacationBar({
    duration,
    gridColumnStart,
    gridColumnEnd,
    isMenuOpen,
    menuAnchor,
    onMenuOpen,
    onMenuClose,
    onSelectionCancel,
}: {
    duration: Duration;
    gridColumnStart: number;
    gridColumnEnd: number;
    isMenuOpen: boolean;
    menuAnchor: VirtualAnchor | null;
    onMenuOpen: (vacationId: number, anchor: VirtualAnchor) => void;
    onMenuClose: () => void;
    onSelectionCancel: () => void;
}) {
    const dispatch = useAppDispatch();
    const profile = useAppSelector((selector) => selector.profile);
    const generalFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter);
    const vacationStatesFilter = useAppSelector((selector) => selector.vacationsReport.filter.propertyFilter.selectedVacationStates);
    const navigate = useNavigate();
    const intl = useIntl();
    const [pendingAction, setPendingAction] = useState<VacationAction | null>(null);
    const [ignoreOtherApprovers, setIgnoreOtherApprovers] = useState(false);

    const handleCloseMenu = onMenuClose;

    const canApproveOrDeclineAnyVacations = canApproveDeclineAnyVacations(profile);
    const currentUserApprover = duration.approvers.find((approver) => approver.employeeId === profile.id);
    const canEditVacation = duration.employeeId === profile.id
        || canApproveOrDeclineAnyVacations
        || canEditVacationWithoutApproval(profile);
    const canCancelVacation = duration.employeeId === profile.id || canApproveOrDeclineAnyVacations;
    const isWaitingApproval = isVacationWaitingApproval(duration.state);
    const isApprovedByCurrentUser = isWaitingApproval && currentUserApprover?.approved;
    const canApproveOrDeclineAsRegularApprover = duration.employeeId !== profile.id && currentUserApprover != null;
    const canApproveVacation = isWaitingApproval
        && (canApproveOrDeclineAnyVacations || (canApproveOrDeclineAsRegularApprover && !currentUserApprover.approved));
    const canDeclineVacation = isWaitingApproval
        && !isApprovedByCurrentUser
        && (canApproveOrDeclineAnyVacations || canApproveOrDeclineAsRegularApprover);

    const getApproveForce = () => {
        if (!canApproveOrDeclineAnyVacations) {
            return false;
        }

        return ignoreOtherApprovers;
    };

    const refreshVacations = async () => {
        const { startDate, endDate } = generalFilter.dateRangeFilter;
        if (!startDate || !endDate) {
            return;
        }

        const selectedEmployees = generalFilter.propertyFilter.selectedEmployees ?? [];
        const canViewOtherVacations = canSeeOtherVacations(profile);
        const employeeIds = canViewOtherVacations
            ? selectedEmployees.map((employee) => employee.originalId as number)
            : [profile.id].filter((id) => id > 0);

        await dispatch(getVacations({
            dateFrom: startDate,
            dateTo: endDate,
            employees: employeeIds,
            states: vacationStatesFilter?.map((state) => state.originalId as number) ?? [],
        })).unwrap();
    };

    const handleConfirm = async (force?: boolean) => {
        if (!pendingAction) {
            return;
        }

        if (pendingAction === 'cancel') {
            await dispatch(cancelVacation(duration.id)).unwrap();
        }
        if (pendingAction === 'approve') {
            await dispatch(approveVacation({
                vacationId: duration.id,
                force: force ?? getApproveForce(),
            })).unwrap();
        }
        if (pendingAction === 'decline') {
            await dispatch(declineVacation(duration.id)).unwrap();
        }

        await refreshVacations();
        setPendingAction(null);
        setIgnoreOtherApprovers(false);
        handleCloseMenu();
    };

    const actions = useMemo(() => {
        const items: Array<{
            key: string;
            label: string;
            icon: React.ReactNode;
            onClick: () => void;
        }> = [];

        if (canEditVacation) {
            items.push({
                key: 'edit',
                label: intl.formatMessage({ id: Lx.General.EDIT }),
                icon: <SVGS.EditIcon />,
                onClick: () => {
                    navigate(NAVIGATION_ROUTES.EDIT_VACATION, {
                        state: {
                            vacationId: duration.id,
                        }
                    });
                    handleCloseMenu();
                }
            });
        }

        if (canCancelVacation) {
            items.push({
                key: 'cancel',
                label: intl.formatMessage({ id: Lx.General.CANCEL }),
                icon: <SVGS.DeleteIcon />,
                onClick: () => {
                    setPendingAction('cancel');
                    handleCloseMenu();
                }
            });
        }

        if (canApproveVacation) {
            items.push({
                key: 'approve',
                label: intl.formatMessage({ id: Lx.Vacation.APPROVE }),
                icon: <SVGS.ApproveIcon />,
                onClick: () => {
                    setPendingAction('approve');
                    handleCloseMenu();
                }
            });
        }

        if (canDeclineVacation) {
            items.push({
                key: 'decline',
                label: intl.formatMessage({ id: Lx.Vacation.DECLINE }),
                icon: <SVGS.DisapproveIcon />,
                onClick: () => {
                    setPendingAction('decline');
                    handleCloseMenu();
                }
            });
        }

        return items;
    }, [canApproveVacation, canCancelVacation, canDeclineVacation, canEditVacation, duration.id, intl, navigate]);

    const getDialogConfig = () => {
        switch (pendingAction) {
            case 'approve':
                return {
                    title: intl.formatMessage({ id: Lx.Vacation.APPROVE_TITLE }),
                    message: ignoreOtherApprovers
                        ? intl.formatMessage({ id: Lx.Vacation.APPROVE_FORCE_CONFIRM_MESSAGE })
                        : intl.formatMessage({ id: Lx.Vacation.APPROVE_CONFIRM_MESSAGE }),
                    displayMessage: '',
                    actionLabel: intl.formatMessage({ id: Lx.Vacation.APPROVE }),
                    cancelLabel: intl.formatMessage({ id: Lx.General.CANCEL }),
                    icon: <SVGS.ApproveIcon style={{ width: '32px', height: '32px' }} />,
                };
            case 'decline':
                return {
                    title: intl.formatMessage({ id: Lx.Vacation.DECLINE_TITLE }),
                    message: intl.formatMessage({ id: Lx.Vacation.DECLINE_CONFIRM_MESSAGE }),
                    displayMessage: intl.formatMessage({ id: Lx.Vacation.DECLINE_CONFIRM_MESSAGE }),
                    actionLabel: intl.formatMessage({ id: Lx.Vacation.DECLINE }),
                    cancelLabel: intl.formatMessage({ id: Lx.General.CANCEL }),
                    icon: <SVGS.DisapproveIcon style={{ width: '32px', height: '32px' }} />,
                };
            case 'cancel':
            default:
                return {
                    title: intl.formatMessage({ id: Lx.Vacation.CANCEL_TITLE }),
                    message: intl.formatMessage({ id: Lx.Vacation.CANCEL_CONFIRM_MESSAGE }),
                    displayMessage: intl.formatMessage({ id: Lx.Vacation.CANCEL_CONFIRM_MESSAGE }),
                    actionLabel: intl.formatMessage({ id: Lx.General.CANCEL }),
                    cancelLabel: intl.formatMessage({ id: Lx.General.CLOSE }),
                    icon: <SVGS.DeleteIcon style={{ width: '32px', height: '32px' }} />,
                };
        }
    };

    const dialogConfig = getDialogConfig();

    const handleListKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Tab' || event.key === 'Escape') {
            handleCloseMenu();
        }
    };

    return (
        <>
            <div
                className={classNames(styles.bar, {
                    [styles.approved]: duration.state === VacationState.Approved,
                    [styles.waitapprove]: isWaitingApproval && !isApprovedByCurrentUser,
                    [styles.approvedByCurrentUser]: isApprovedByCurrentUser,
                })}
                data-vacation-selection-interactive='true'
                style={{ gridColumn: `${gridColumnStart} / ${gridColumnEnd}` }}
                onClick={(event) => {
                    event.stopPropagation();
                    onSelectionCancel();
                    if (actions.length === 0) {
                        return;
                    }

                    const target = event.currentTarget;
                    const targetRect = target.getBoundingClientRect();
                    const offsetX = event.clientX - targetRect.left;
                    const offsetY = event.clientY - targetRect.top;

                    onMenuOpen(duration.id, {
                        getBoundingClientRect: () => {
                            const rect = target.getBoundingClientRect();
                            return new DOMRect(rect.left + offsetX, rect.top + offsetY, 0, 0);
                        },
                        contextElement: target,
                    });
                }}
            />
            <Popper
                open={isMenuOpen && actions.length > 0}
                anchorEl={menuAnchor}
                placement={'bottom-start'}
                className={classNames(popupStyles.popper, styles.vacationMenuPopper)}
                transition
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        className={classNames(
                            popupStyles.popperGrow,
                            {
                                [popupStyles.popperGrowBottomStart]: placement === 'bottom-start',
                            }
                        )}
                    >
                        <Paper sx={{ bgcolor: 'var(--background-secondary-not-transparent)', borderRadius: '8px' }}>
                            <ClickAwayListener onClickAway={handleCloseMenu}>
                                <MenuList
                                    onKeyDown={handleListKeyDown}
                                    className={popupStyles.actionMenu}
                                >
                                    {actions.map((action) => (
                                        <MenuItem
                                            key={action.key}
                                            onClick={action.onClick}
                                            className={popupStyles.actionItem}
                                        >
                                            {action.icon}
                                            <span className={popupStyles.iconText}>{action.label}</span>
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
            {pendingAction ? (
                <ActionConfirmDialog
                    open={true}
                    title={dialogConfig.title}
                    message={dialogConfig.displayMessage}
                    actionLabel={dialogConfig.actionLabel}
                    cancelLabel={dialogConfig.cancelLabel}
                    icon={dialogConfig.icon}
                    onCancel={() => {
                        setPendingAction(null);
                        setIgnoreOtherApprovers(false);
                    }}
                    onConfirm={() => handleConfirm()}
                >
                    {pendingAction === 'approve' ? (
                        <div className={styles.approveDialogContent}>
                            {duration.approvers.length > 0 ? (
                                <div className={styles.approversList}>
                                    {duration.approvers.map((approver) => (
                                        <div className={styles.approverItem} key={approver.employeeId}>
                                            <span className={styles.approverName}>{approver.employeeName}</span>
                                            <span
                                                className={classNames(styles.approverStatus, {
                                                    [styles.approverStatusApproved]: approver.approved,
                                                    [styles.approverStatusPending]: !approver.approved,
                                                })}
                                            >
                                                {approver.approved
                                                    ? intl.formatMessage({ id: Lx.Vacation.APPROVER_APPROVED })
                                                    : intl.formatMessage({ id: Lx.Vacation.APPROVER_NOT_APPROVED })}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            {canApproveOrDeclineAnyVacations ? (
                                <TSwitch
                                    name='ignore-other-approvers'
                                    checked={ignoreOtherApprovers}
                                    onChange={setIgnoreOtherApprovers}
                                    label={intl.formatMessage({ id: Lx.Vacation.IGNORE_OTHER_APPROVERS })}
                                    labelProps={{ className: styles.ignoreApproversSwitch }}
                                />
                            ) : null}
                            <div className={styles.approveDialogMessage}>{dialogConfig.message}</div>
                        </div>
                    ) : null}
                </ActionConfirmDialog>
            ) : null}
        </>
    );
}

function Row({
    row,
    days,
    selection,
    onSelectionStart,
    onSelectionExtend,
    openMenu,
    onMenuOpen,
    onMenuClose,
    onSelectionCancel,
}: {
    row: GroupedDiagramItem;
    days: VacationDiagramDay[];
    selection: DraftSelection | null;
    onSelectionStart: (employeeId: number, dayIndex: number) => void;
    onSelectionExtend: (employeeId: number, dayIndex: number) => void;
    openMenu: OpenMenuState;
    onMenuOpen: (vacationId: number, anchor: VirtualAnchor) => void;
    onMenuClose: () => void;
    onSelectionCancel: () => void;
})
{
    // map date to index for grid spanning
    const idxByDate = useMemo(() => {
        const m = new Map<string, number>();
        days.forEach((d, i) => m.set(d.date.format('YYYY-MM-DD'), i));
        return m;
    }, [days]);

    // clamp span into visible days
    const clamp = (start: moment.Moment, end: moment.Moment) => {
        const s = moment.max(start.clone().startOf('day'), days[0].date.clone().startOf('day'));
        const e = moment.min(end.clone().startOf('day'), days[days.length - 1].date.clone().startOf('day'));
        return e.isBefore(s) ? null : { s, e };
    };

    const previewRange = selection?.employeeId === row.employeeId
        ? {
            startIndex: Math.min(selection.anchorIndex, selection.currentIndex),
            endIndex: Math.max(selection.anchorIndex, selection.currentIndex),
        }
        : null;

    return (
        <div
            className={styles.row}
        >
            <div className={styles.rowNameCell} title={row.employeeName}>
                <span className={styles.rowNameText} onClick={onSelectionCancel}>{row.employeeName}</span>
            </div>

            {/* background cells (weekend/short) */}
            {days.map((d, index) => {
                const isSelectionDisabled = !isVacationCreationDateAvailable(d.date);

                return (
                    <div
                        key={d.date.toISOString()}
                        data-vacation-selection-interactive='true'
                        className={classNames(styles.dayBgCell, {
                            [styles.off]: d.isDayOff,
                            [styles.short]: d.isShort,
                            [styles.selectionDisabled]: isSelectionDisabled,
                            [styles.lastUnavailableDayCell]: d.date.isSame(moment(), 'day'),
                            [styles.lastDayCell]: index === days.length - 1,
                            [styles.firstDayCell]: index === 0,
                        })}
                        onClick={isSelectionDisabled ? onSelectionCancel : () => onSelectionStart(row.employeeId, index)}
                        onMouseEnter={isSelectionDisabled ? undefined : () => onSelectionExtend(row.employeeId, index)}
                    />
                );
            })}

            {previewRange ? (
                <div
                    className={classNames(styles.bar, styles.selectionBar)}
                    style={{ gridColumn: `${1 + 1 + previewRange.startIndex} / ${1 + 1 + previewRange.endIndex + 1}` }}
                />
            ) : null}

            {/* bars */}
            {Array.isArray(row.durations) && row.durations.map((dur) => {
                const c = clamp(dur.dateFrom, dur.dateTo);
                if (!c) return null;

                const startIdx = idxByDate.get(c.s.format('YYYY-MM-DD')) ?? 0;
                const endIdx = idxByDate.get(c.e.format('YYYY-MM-DD')) ?? 0;

                // +1 name col; +1 because CSS grid end is exclusive
                const gridColumnStart = 1 + 1 + startIdx;
                const gridColumnEnd = 1 + 1 + endIdx + 1;

                return (
                    <VacationBar
                        key={`${dur.id}-${dur.dateFrom.format('YYYY-MM-DD')}-${dur.dateTo.format('YYYY-MM-DD')}`}
                        duration={dur}
                        gridColumnStart={gridColumnStart}
                        gridColumnEnd={gridColumnEnd}
                        isMenuOpen={openMenu?.vacationId === dur.id}
                        menuAnchor={openMenu?.vacationId === dur.id ? openMenu.anchor : null}
                        onMenuOpen={onMenuOpen}
                        onMenuClose={onMenuClose}
                        onSelectionCancel={onSelectionCancel}
                    />
                );
            })}
        </div>
    );
}

export function VacationDiagramHeader({
    days,
    onSelectionCancel,
}: {
    days: VacationDiagramDay[];
    onSelectionCancel: () => void;
}) {
    return (
        <div
            className={styles.headerRow}
        >
            <div className={styles.headerNameCell} onClick={onSelectionCancel}>
                <div className={styles.headerNameCellInside}/>
            </div>
            {days.map((d, index) => (
                <div key={d.date.format('YYYY-MM-DD')}
                    className={classNames(styles.headerDateCell, {
                        [styles.lastUnavailableDayHeaderCell]: d.date.isSame(moment(), 'day'),
                        [styles.lastDayHeaderCell]: index === days.length - 1,
                        [styles.firstDayHeaderCell]: index === 0
                    })}
                    onClick={onSelectionCancel}>
                    {d.date.format('DD.MM')}
                </div>
            ))}
        </div>
    );
}

export default function VacationDiagramRedesigned({
    employees,
    days,
    onCreateRange,
}: Props) {
    const selectionRef = useRef<DraftSelection | null>(null);
    const [selection, setSelection] = useState<DraftSelection | null>(null);
    const [openMenu, setOpenMenu] = useState<OpenMenuState>(null);
    const {
        headerScrollRef,
        bodyScrollRef,
        bottomScrollRef,
        bottomScrollbar,
        handleScrollSync,
    } = useBottomScrollbar(employees.length, days.length);

    const clearSelection = useCallback(() => {
        selectionRef.current = null;
        setSelection(null);
    }, []);

    useEffect(() => {
        const handleDocumentPointerDown = (event: PointerEvent) => {
            if (!selectionRef.current) {
                return;
            }

            const target = event.target;
            const targetElement = target instanceof Element
                ? target
                : target instanceof Node
                    ? target.parentElement
                    : null;

            if (targetElement?.closest('[data-vacation-selection-interactive="true"]')) {
                return;
            }

            clearSelection();
        };

        document.addEventListener('pointerdown', handleDocumentPointerDown);
        return () => {
            document.removeEventListener('pointerdown', handleDocumentPointerDown);
        };
    }, [clearSelection]);

    const handleMenuClose = useCallback(() => {
        setOpenMenu(null);
    }, []);

    const handleMenuOpen = useCallback((vacationId: number, anchor: VirtualAnchor) => {
        setOpenMenu({ vacationId, anchor });
    }, []);

    const finalizeSelection = useCallback((activeSelection: DraftSelection) => {
        if (!activeSelection) {
            return;
        }

        const employee = employees.find((item) => item.employeeId === activeSelection.employeeId);
        const startDay = days[Math.min(activeSelection.anchorIndex, activeSelection.currentIndex)];
        const endDay = days[Math.max(activeSelection.anchorIndex, activeSelection.currentIndex)];
        clearSelection();

        if (!employee || !startDay || !endDay) {
            return;
        }

        onCreateRange(employee, startDay.date.clone(), endDay.date.clone());
    }, [clearSelection, days, employees, onCreateRange]);

    const handleSelectionStart = useCallback((employeeId: number, dayIndex: number) => {
        const day = days[dayIndex];
        if (!day || !isVacationCreationDateAvailable(day.date)) {
            clearSelection();
            return;
        }

        const activeSelection = selectionRef.current;

        if (activeSelection?.pendingConfirmation && activeSelection.employeeId === employeeId) {
            const nextSelection = {
                ...activeSelection,
                currentIndex: dayIndex,
            };

            finalizeSelection(nextSelection);
            return;
        }

        const nextSelection = {
            employeeId,
            anchorIndex: dayIndex,
            currentIndex: dayIndex,
            pendingConfirmation: true,
        };

        selectionRef.current = nextSelection;
        setSelection(nextSelection);
    }, [clearSelection, days, finalizeSelection]);

    const handleSelectionExtend = useCallback((employeeId: number, dayIndex: number) => {
        const day = days[dayIndex];
        if (!day || !isVacationCreationDateAvailable(day.date)) {
            return;
        }

        const activeSelection = selectionRef.current;
        if (!activeSelection || activeSelection.employeeId !== employeeId || activeSelection.currentIndex === dayIndex) {
            return;
        }

        const nextSelection = {
            ...activeSelection,
            currentIndex: dayIndex,
        };

        selectionRef.current = nextSelection;
        setSelection(nextSelection);
    }, [days]);

    const gridVars = useMemo<React.CSSProperties & { '--days-count': number }>(
        () => ({
            '--days-count': days.length,
        }),
        [days.length]
    );

    return (
        <div className={styles.diagramShell} style={gridVars}>
            <div className={styles.tableFrame}>
                <div
                    className={styles.headerScroll}
                    ref={headerScrollRef}
                    onScroll={handleScrollSync('header')}
                >
                    <div>
                        <VacationDiagramHeader days={days} onSelectionCancel={clearSelection} />
                    </div>
                </div>
                <div
                    className={styles.viewport}
                    ref={bodyScrollRef}
                    onScroll={handleScrollSync('body')}
                >
                    <div className={styles.grid}>
                        {employees.map((e) => (
                            <Row
                                key={e.employeeId}
                                row={e}
                                days={days}
                                selection={selection}
                                onSelectionStart={handleSelectionStart}
                                onSelectionExtend={handleSelectionExtend}
                                openMenu={openMenu}
                                onMenuOpen={handleMenuOpen}
                                onMenuClose={handleMenuClose}
                                onSelectionCancel={clearSelection}
                            />
                        ))}
                    </div>
                </div>
                {bottomScrollbar.show ? (
                    <div
                        ref={bottomScrollRef}
                        aria-hidden='true'
                        className={styles.bottomScrollbar}
                        style={{
                            left: bottomScrollbar.fixedLeft,
                            width: bottomScrollbar.fixedWidth,
                        }}
                        onScroll={handleScrollSync('bottom')}>
                        <div
                            className={styles.bottomScrollbarContent}
                            style={{ width: bottomScrollbar.contentWidth }}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}
